import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AmpelBadge } from "@/components/AmpelBadge"
import { Button, Select, Input, Textarea } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"
import {
  createWartung,
  deleteWartung,
  dupliziereWartung,
  erstelleWartungServiceticket,
  fetchAnlagen,
  fetchWartung,
  fetchWartungsarten,
  updateWartung,
  type WartungEingabe,
} from "@/lib/api"

const LEER: WartungEingabe = {
  wartungsartId: 0,
  komponenteId: null,
  objektId: null,
  anlageId: null,
  wartungsdatum: null,
  status: null,
  aktiv: true,
  ergebnis: null,
  protokollPaperlessId: null,
}

/** Wartungs-/Inspektions-Detailformular (AP-3.4) — Ablaufdatum/Ampel serverseitig. */
export function WartungDetailPage() {
  const params = useParams<{ id: string }>()
  const istNeu = params.id === "neu"
  const wartungId = istNeu ? null : Number(params.id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [form, setForm] = useState<WartungEingabe>(LEER)

  const { data: wartung } = useQuery({
    queryKey: ["wartungen", wartungId],
    queryFn: () => fetchWartung(wartungId!),
    enabled: wartungId !== null,
  })
  const { data: arten } = useQuery({ queryKey: ["wartungsarten"], queryFn: fetchWartungsarten })
  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })

  useEffect(() => {
    if (wartung) setForm(wartung)
  }, [wartung])

  const speichern = useMutation({
    mutationFn: () => (istNeu ? createWartung(form) : updateWartung(wartungId!, form)),
    onSuccess: (ergebnis) => {
      queryClient.invalidateQueries({ queryKey: ["wartungen"] })
      toast.success("Wartung gespeichert.")
      if (istNeu) navigate(`/maschinen/wartungen/${ergebnis.id}`)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deleteWartung(wartungId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wartungen"] })
      toast.success("Wartung gelöscht.")
      navigate("/maschinen/wartungen")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht gelöscht werden.")),
  })

  const duplizieren = useMutation({
    mutationFn: () => dupliziereWartung(wartungId!),
    onSuccess: (neu) => {
      toast.success("Wartung dupliziert.")
      navigate(`/maschinen/wartungen/${neu.id}`)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Wartung konnte nicht dupliziert werden.")),
  })

  const serviceticket = useMutation({
    mutationFn: () => erstelleWartungServiceticket(wartungId!),
    onSuccess: () => toast.success("Serviceticket erstellt."),
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Serviceticket konnte nicht erstellt werden.")),
  })

  return (
    <div>
      <nav className="mb-4 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link
          to="/maschinen/wartungen"
          className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          ← Zurück zu Wartungen
        </Link>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {istNeu ? "Neue Wartung/Inspektion" : `Wartung #${wartungId}`}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          02-W — dieselbe Fristen-Engine wie Prüfungen, konsistent schaltjahrkorrekt (§5).
        </p>
        {wartung && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="text-slate-600">Status:</span>
            <AmpelBadge ampel={wartung.ampel} />
            <span className="text-slate-500">Ablaufdatum: {wartung.ablaufdatum ?? "—"}</span>
          </div>
        )}
      </header>

      <section className="max-w-xl rounded-lg border border-slate-200 bg-white p-6">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <label className="block text-sm">
            Wartungs-/Inspektionsart
            <Select
              required
              value={form.wartungsartId || ""}
              onChange={(e) => setForm({ ...form, wartungsartId: Number(e.target.value) })}
              className="mt-1"
            >
              <option value="">— auswählen —</option>
              {arten?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.titel}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-sm">
            Anlage
            <Select
              value={form.anlageId ?? ""}
              onChange={(e) =>
                setForm({ ...form, anlageId: e.target.value ? Number(e.target.value) : null })
              }
              className="mt-1"
            >
              <option value="">—</option>
              {anlagen?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bezeichnung}
                </option>
              ))}
            </Select>
          </label>
          <label className="block text-sm">
            Wartungsdatum
            <Input
              type="date"
              value={form.wartungsdatum ?? ""}
              onChange={(e) => setForm({ ...form, wartungsdatum: e.target.value || null })}
              className="mt-1"
            />
          </label>
          <label className="block text-sm">
            Status
            <Input
              value={form.status ?? ""}
              onChange={(e) => setForm({ ...form, status: e.target.value || null })}
              className="mt-1"
            />
          </label>
          <label className="block text-sm">
            Ergebnis
            <Textarea
              value={form.ergebnis ?? ""}
              onChange={(e) => setForm({ ...form, ergebnis: e.target.value })}
              className="mt-1"
              rows={3}
            />
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="submit" variant="primary" disabled={speichern.isPending}>
              Speichern
            </Button>
            {!istNeu && (
              <>
                <Button type="button" variant="secondary" onClick={() => duplizieren.mutate()}>
                  Wartung duplizieren
                </Button>
                <Button type="button" variant="secondary" onClick={() => serviceticket.mutate()}>
                  Serviceticket erstellen
                </Button>
                <Button type="button" variant="destructive" onClick={() => loeschen.mutate()}>
                  Löschen
                </Button>
              </>
            )}
          </div>
          {serviceticket.isSuccess && (
            <p className="text-sm text-green-700">
              Serviceticket erstellt: {serviceticket.data.ticketKey}
            </p>
          )}
        </form>
      </section>
    </div>
  )
}
